import React, { Fragment, useMemo } from 'react';
import {
  HeaderGroup,
  useExpanded,
  useFlexLayout,
  useSortBy,
  useTable,
  UseTableOptions,
  // ColumnInstance,
} from 'react-table';

// Utils
import { classList } from '../../util/util';

type TableData = {
  tableName: string;
  renderRow: ({ row, index }: any) => any;
  renderExpanded?: ({ row }: any) => any;
  renderRowSeparator?: ({ row, index }: any) => any;
  classPrefix?: string | null;
  autoReset?: boolean;
} & UseTableOptions<object>;

interface IHeaderGroupExtras extends HeaderGroup {
  className?: string;
  headerClassName?: string;
}

// interface IColumnInstance extends ColumnInstance {
//   headerClassName?: string;
// }

const ReactTable = (props: TableData) => {
  // Default values for all columns
  const defaultColumn = useMemo(
    () => ({
      // When using the useFlexLayout:
      minWidth: 25, // minWidth is only used as a limit for resizing
      width: 50, // width is used for both the flex-basis and flex-grow
      maxWidth: 200, // maxWidth is only used as a limit for resizing
    }),
    []
  );

  // Props
  const {
    classPrefix,
    columns,
    data,
    initialState,
    tableName,
    renderExpanded,
    renderRow,
    renderRowSeparator,
    autoReset
  } = props;

  // Get react-table functions we need.
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState,
      // Stops state from resetting content in renderExpanded(). This keeps the
      // expanded content from disappearing every 15 seconds when we check for
      // new data
      autoResetExpanded: autoReset ?? false,
      // Stops state from resetting content when sorting.
      autoResetSortBy: false,
      expandSubRows: false,
    },
    useSortBy,
    useExpanded,
    useFlexLayout
  );

  const numRows = rows.length;

  return (
    <div
      // We want the role from the props but not the min-width
      {...getTableProps({ style: { minWidth: 'unset' } })} // { style: { minWidth: 'unset' } }
      className={`${classPrefix}table ${classPrefix}${tableName}`}
    >
      <div className={classList(`${classPrefix}thead`)}>
        {headerGroups.map((headerGroup, index) => (
          <div
            {...headerGroup.getHeaderGroupProps()}
            className={classList(
              `${classPrefix}header ${classPrefix}header-${index}`
            )}
          >
            {headerGroup.headers.map((column: IHeaderGroupExtras) => {
              const { render, getHeaderProps, getSortByToggleProps } = column;
              return (
                <div
                  {...getHeaderProps(getSortByToggleProps())}
                  className={classList(`${classPrefix}header__cell`)}
                >
                  {render('Header')}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div {...getTableBodyProps()} className={`${classPrefix}tbody`}>
        {rows.map((row, index) => {
          prepareRow(row);
          return (
            // Can't do: <React.Fragment {...row.getRowProps()}>
            // @see https://github.com/tannerlinsley/react-table/issues/2107
            <Fragment key={`${tableName}-${index}`}>
              {renderRow({ row, index })}
              {row.isExpanded && renderExpanded ? (
                <div className="expander">{renderExpanded({ row, index })}</div>
              ) : null}
              {renderRowSeparator &&
                renderRowSeparator({ row, numRows, index })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ReactTable;
